import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Review-queue API client (T-0045 / E02-F05).
 *
 * Three surfaces under `/api/v1/engagements/:engagement_id/review/proposals`:
 *   - GET  /                  — list with bucket/decision/proposal_type filters
 *   - GET  /:id/source         — click-through to the cited passage(s)
 *   - POST /:id/decide         — apply a submittals verb
 *
 * Zod schemas mirror the apps/api DTOs (services/review-proposals.ts). The
 * proposed_value + target_ref + citations.span columns are jsonb, so we
 * keep them as `z.unknown()` and let the consumer (the page) cast as it
 * renders.
 */

export const CitationDtoSchema = z.object({
  id: z.string(),
  document_id: z.string(),
  document_title: z.string(),
  page_number: z.number().nullable(),
  section_path: z.string().nullable(),
  span: z.unknown(),
});
export type CitationDto = z.infer<typeof CitationDtoSchema>;

export const ExistingMatchSchema = z.object({
  register_entry_id: z.string(),
  name: z.string(),
  /** Field-level differences; empty when the proposal restates the item as is. */
  differences: z.record(z.object({ current: z.string().nullable(), proposed: z.string() })),
});

export const RoutingProposalSchema = z.object({
  id: z.string(),
  ai_proposal_id: z.string(),
  bucket: z.enum(['auto_apply', 'review', 'reject']),
  is_baseline: z.boolean(),
  confidence: z.number(),
  proposal_type: z.string(),
  /** T-0219: "Assessment suggestion" for the stage-2 types; null otherwise. */
  label: z.string().nullable().optional(),
  proposed_value: z.unknown(),
  target_ref: z.unknown(),
  citations: z.array(CitationDtoSchema),
  /**
   * C2-52: the committed deliverable a pending `edl_item` restates, checked
   * against the live register on every read. Approving it updates that
   * entry; it never adds a second copy.
   */
  existing_match: ExistingMatchSchema.nullable().optional(),
  decision: z.enum(['pending', 'approved', 'approved_as_noted', 'revise', 'rejected']),
  decision_at: z.string().nullable(),
  decision_by_user_id: z.string().nullable(),
  decision_rationale: z.string().nullable(),
  /** True when the system approved this under the firm auto-approve policy. */
  auto_approved: z.boolean().optional(),
  override_record_id: z.string().nullable(),
  committed_register_entry_id: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type RoutingProposal = z.infer<typeof RoutingProposalSchema>;
export type ExistingMatch = z.infer<typeof ExistingMatchSchema>;

export const RoutingProposalListSchema = z.object({
  items: z.array(RoutingProposalSchema),
  next_cursor: z.string().nullable(),
});
export type RoutingProposalList = z.infer<typeof RoutingProposalListSchema>;

export const SourceCitationDtoSchema = z.object({
  citation_id: z.string(),
  document_id: z.string(),
  document_title: z.string(),
  page_number: z.number().nullable(),
  section_path: z.string().nullable(),
  /** `{start, end, text}` inside `chunk_text` when the quote was located there. */
  span: z.unknown(),
  /** The cited chunk id and the model's verified quote (null before migration 0125). */
  chunk_id: z.string().nullable().optional(),
  quote: z.string().nullable().optional(),
  chunk_text: z.string().nullable(),
  chunk_type: z.string().nullable(),
});
export type SourceCitationDto = z.infer<typeof SourceCitationDtoSchema>;

export const SourceViewSchema = z.object({
  proposal_id: z.string(),
  ai_proposal_id: z.string(),
  citations: z.array(SourceCitationDtoSchema),
});
export type SourceView = z.infer<typeof SourceViewSchema>;

export const DecideResultSchema = z.object({
  id: z.string(),
  decision: z.enum(['pending', 'approved', 'approved_as_noted', 'revise', 'rejected']),
  committed_register_entry_id: z.string().nullable(),
  override_record_id: z.string().nullable(),
});
export type DecideResult = z.infer<typeof DecideResultSchema>;

export type DecideVerb = 'approved' | 'approved_as_noted' | 'revise' | 'rejected';

export interface DecideProposalInput {
  verb: DecideVerb;
  decision_rationale?: string;
  override_tag?: string;
  edited_value?: Record<string, unknown>;
}

export interface ListProposalsFilters {
  bucket?: 'auto_apply' | 'review' | 'reject';
  decision?: 'pending' | 'approved' | 'approved_as_noted' | 'revise' | 'rejected';
  proposal_type?: string;
  auto_approved?: boolean;
  cursor?: string;
  limit?: number;
}

export function listReviewProposals(
  engagementId: string,
  filters: ListProposalsFilters = {},
): Promise<RoutingProposalList> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/review/proposals`,
    RoutingProposalListSchema,
    { query: filters as Record<string, string | number | boolean | undefined> },
  );
}

/**
 * GET /api/v1/engagements/:engagement_id/review/proposals/:id — single-hop
 * fetch shipped in T-0122 (PR #141). T-0115 review-state modal previously
 * pulled the whole queue and filtered client-side; this lets the modal
 * fetch one row with one request. RLS via engagement membership; cross-
 * engagement / unknown / off-team callers surface as 404 (existence-leak
 * gate).
 */
export function getReviewProposal(
  engagementId: string,
  proposalId: string,
): Promise<RoutingProposal> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/review/proposals/${encodeURIComponent(
      proposalId,
    )}`,
    RoutingProposalSchema,
  );
}

export function useReviewProposalQuery(
  engagementId: string | undefined,
  proposalId: string | undefined,
) {
  return useQuery({
    queryKey: ['review-proposals', engagementId, 'one', proposalId] as const,
    queryFn: () => getReviewProposal(engagementId!, proposalId!),
    enabled: Boolean(engagementId) && Boolean(proposalId),
  });
}

export function getProposalSource(engagementId: string, proposalId: string): Promise<SourceView> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/review/proposals/${encodeURIComponent(
      proposalId,
    )}/source`,
    SourceViewSchema,
  );
}

export function decideProposal(
  engagementId: string,
  proposalId: string,
  input: DecideProposalInput,
  idempotencyKey?: string,
): Promise<DecideResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/review/proposals/${encodeURIComponent(
      proposalId,
    )}/decide`,
    DecideResultSchema,
    {
      method: 'POST',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

/** Rows per request when reading the whole queue; the API caps `limit` at 200. */
export const QUEUE_PAGE_SIZE = 200;
/** Pages read for one queue view; past this the newest rows are shown and `truncated` is set. */
export const QUEUE_MAX_PAGES = 10;

export interface RoutingProposalQueue extends RoutingProposalList {
  /** True when the queue had more pages than QUEUE_MAX_PAGES; only the newest rows are listed. */
  truncated: boolean;
}

/**
 * Every proposal the filters match, newest first. The API pages (50 by
 * default) and the review screen groups the whole queue by type, so a single
 * page hid everything older than the newest 50 rows: contract facts and
 * change-order revisions from the baseline read were never reachable
 * ("50 of 279", Oregon Metro test, 13 October 2026). Follows `next_cursor`
 * at QUEUE_PAGE_SIZE per request.
 */
export async function listAllReviewProposals(
  engagementId: string,
  filters: ListProposalsFilters = {},
): Promise<RoutingProposalQueue> {
  const items: RoutingProposal[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < QUEUE_MAX_PAGES; page++) {
    const res = await listReviewProposals(engagementId, {
      ...filters,
      limit: QUEUE_PAGE_SIZE,
      cursor,
    });
    items.push(...res.items);
    if (!res.next_cursor) return { items, next_cursor: null, truncated: false };
    cursor = res.next_cursor;
  }
  return { items, next_cursor: cursor ?? null, truncated: true };
}

export function useProposalQueueQuery(
  engagementId: string | undefined,
  filters: ListProposalsFilters = {},
  options: {
    /** Poll the queue (ms) — while a revised proposal's re-read is awaited. */
    refetchInterval?: number | false;
  } = {},
) {
  return useQuery({
    queryKey: ['review-proposals', engagementId, filters] as const,
    queryFn: () => listAllReviewProposals(engagementId!, filters),
    enabled: Boolean(engagementId),
    refetchInterval: options.refetchInterval ?? false,
  });
}

export function useProposalSourceQuery(
  engagementId: string | undefined,
  proposalId: string | undefined,
) {
  return useQuery({
    queryKey: ['review-proposals', engagementId, 'source', proposalId] as const,
    queryFn: () => getProposalSource(engagementId!, proposalId!),
    enabled: Boolean(engagementId) && Boolean(proposalId),
  });
}

export function useDecideProposalMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      proposalId,
      input,
      idempotencyKey,
    }: {
      proposalId: string;
      input: DecideProposalInput;
      idempotencyKey?: string;
    }) =>
      // Fresh key per submission: retry-safe for THIS attempt without
      // poisoning later decides of the same proposal (the server no longer
      // derives a per-proposal fallback key for exactly that reason).
      decideProposal(engagementId!, proposalId, input, idempotencyKey ?? crypto.randomUUID()),
    onSuccess: () => {
      if (engagementId) {
        // Invalidate the queue + the registers cache (an Approved /
        // Approved-as-Noted decision can mutate a register entry).
        void qc.invalidateQueries({ queryKey: ['review-proposals', engagementId] });
        void qc.invalidateQueries({ queryKey: ['registers', engagementId] });
        // Commit-on-approve writes land on these surfaces; checkpoints has
        // a 30s staleTime so an explicit invalidate keeps it fresh.
        void qc.invalidateQueries({ queryKey: ['checkpoints', engagementId] });
        void qc.invalidateQueries({ queryKey: ['edl', engagementId] });
      }
    },
  });
}
